import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Stack } from '@fluentui/react';
import { stackTokens15 } from '../../utils';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import { toastError } from '../../utils/toast';
import { BillitInvoice } from '../billit-invoices/types';
import PageHeader from '../../components/parts/PageHeader';
import BillitInvoicesAssignedOverview from './components/BillitInvoicesAssignedOverview';

const BillitInvoicesAssigned = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [invoices, setInvoices] = useState<BillitInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const limit = 50;

  const fetchInvoices = async (currentOffset = 0, append = false) => {
    const isInitial = currentOffset === 0 && !append;
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/assigned/me?limit=${limit}&offset=${currentOffset}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newInvoices = data.invoices || [];
      const totalCount = data.total ?? 0;

      if (append) {
        setInvoices(prev => [...prev, ...newInvoices]);
      } else {
        setInvoices(newInvoices);
      }
      setTotal(totalCount);
      setOffset(currentOffset);
      setShowLoadMore(currentOffset + newInvoices.length < totalCount);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch invoices';
      dispatch(
        throwError({
          module: 'Billit Invoices Assigned',
          message: errorMessage,
          level: SeverityLevel.Critical,
        }),
      );
      toastError(errorMessage);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices(0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadMoreCallback = () => {
    if (loading || loadingMore) return;
    const nextOffset = offset + limit;
    if (nextOffset >= total) return;
    fetchInvoices(nextOffset, true);
  };

  const refetch = () => {
    fetchInvoices(0, false);
  };

  if (!isAuthenticated) return <p>Verboden</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title="Toegewezen Billit Facturen"
          iconName="Billit"
          loading={loading}
        />
      </div>

      <BillitInvoicesAssignedOverview
        invoices={invoices}
        loading={loading}
        loadingMore={loadingMore}
        refetch={refetch}
        total={total}
        showLoadMore={showLoadMore}
        loadMoreCallback={loadMoreCallback}
      />
    </Stack>
  );
};

export default BillitInvoicesAssigned;
